import React, { useState } from "react";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import {
  Dialog,
  DialogTitle,
  Box,
  IconButton,
  Snackbar,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import Button from "../button";
import Typography from "../typography";
import ShareIcon from "@material-ui/icons/Share";
import CloseIcon from "@material-ui/icons/Close";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { IMatch, IPlayer } from "../../types";

interface ShareMatchButtonProps {
  match: IMatch;
  scorerList: any;
}

const ShareButton = styled(Button)(({ theme }: IStyledArguments) => ({
  background: theme.palette.primary.main,
  color: "white",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  minHeight: "36px",
  padding: "6px 16px",
  "&:hover": {
    background: theme.palette.primary.dark,
  },
}));

const StyledDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    borderRadius: "16px",
    maxWidth: "600px",
    width: "100%",
  },
});

const StyledDialogTitle = styled(DialogTitle)(({ theme }: IStyledArguments) => ({
  background: theme.palette.primary.main,
  color: "white",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: theme.spacing(2, 3),
}));

const ContentBox = styled(Box)(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(3),
}));

const ShareLinkBox = styled(Box)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.backgroundGrey,
  borderRadius: "8px",
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
}));

const LinkText = styled(Typography.Body)(({ theme }: IStyledArguments) => ({
  fontFamily: "monospace",
  fontSize: "0.9rem",
  wordBreak: "break-all",
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(1),
}));

const CopyButton = styled(Button)({
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  justifyContent: "center",
});

const ShareTextBox = styled(Box)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.backgroundGrey,
  borderRadius: "8px",
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  whiteSpace: "pre-wrap",
  fontFamily: "inherit",
  fontSize: "0.95rem",
  lineHeight: 1.6,
}));

const ShareMatchButton: React.FC<ShareMatchButtonProps> = ({ match, scorerList }) => {
  const [open, setOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const shareUrl = `${window.location.origin}/share-match/${match.id}`;

  // Generate share text
  const generateShareText = () => {
    // Local team names and scores (considering if local team plays as home or away)
    const localTeamName = match.isHomeTeamLocal ? match.homeTeam.name : match.awayTeam;
    const opponentTeamName = match.isHomeTeamLocal ? match.awayTeam : match.homeTeam.name;
    const localScore = match.isHomeTeamLocal ? match.score?.home || 0 : match.score?.away || 0;
    const opponentScore = match.isHomeTeamLocal ? match.score?.away || 0 : match.score?.home || 0;

    let text = `⚽ Match Summary\n\n`;
    text += `${localTeamName} ${localScore} - ${opponentScore} ${opponentTeamName}\n\n`;

    // Add scorers
    const scorers = match.playersConfirmed?.filter(
      (player: IPlayer) => scorerList[player.id] > 0
    ) || [];

    if (scorers.length > 0) {
      text += `🏆 Goal Scorers:\n`;
      scorers
        .sort((a: IPlayer, b: IPlayer) => scorerList[b.id] - scorerList[a.id])
        .forEach((player: IPlayer) => {
          const goals = scorerList[player.id];
          const goalEmoji = "⚽".repeat(goals);
          text += `• ${player.name} ${player.surname} ${goalEmoji}\n`;
        });
      text += `\n`;
    }

    if (match.ownGoals > 0) {
      text += `• Own Goal ${match.ownGoals} ⚽\n\n`;
    }

    text += `📅 ${match.date}\n\n`;
    text += `🔗 View full match summary:\n${shareUrl}`;

    return text;
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setSnackbarOpen(true);
  };

  const handleCopyText = () => {
    navigator.clipboard.writeText(generateShareText());
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <>
      <ShareButton onClick={handleOpen} variant="contained">
        <ShareIcon />
        Share Match
      </ShareButton>

      <StyledDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <StyledDialogTitle>
          <Typography.H6 style={{ margin: 0, color: "white" }}>
            Share Match Summary
          </Typography.H6>
          <IconButton
            onClick={handleClose}
            style={{ color: "white", padding: "4px" }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>

        <ContentBox>
          <Typography.Body style={{ marginBottom: "16px", fontWeight: 600 }}>
            Share this link with anyone to view the match summary:
          </Typography.Body>

          <ShareLinkBox>
            <LinkText>{shareUrl}</LinkText>
            <CopyButton onClick={handleCopyLink} color="primary" variant="contained">
              <FileCopyIcon />
              Copy Link
            </CopyButton>
          </ShareLinkBox>

          <Typography.Body style={{ marginTop: "24px", marginBottom: "8px", fontWeight: 600 }}>
            Or copy this text to share:
          </Typography.Body>

          <ShareTextBox>{generateShareText()}</ShareTextBox>

          <CopyButton onClick={handleCopyText} color="primary" variant="contained" style={{ marginTop: "16px" }}>
            <FileCopyIcon />
            Copy Full Text
          </CopyButton>
        </ContentBox>
      </StyledDialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" variant="filled">
          Copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  );
};

export default ShareMatchButton;
